import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  forecastsService,
  GenerateForecastDto,
  UpdateModelParametersDto,
  SLAImpactParams,
  GenerateScheduleParams,
  MarkOutlierDto,
  EditForecastValuesDto,
  SmoothForecastDto,
  CreateAssumptionDto,
  UpdateAssumptionDto,
  ImportJobStatus,
  RegenerationJobStatus,
} from '../services/forecasts.service';

// Re-export types needed by components
export type { ImportJobStatus, RegenerationJobStatus } from '../services/forecasts.service';

// Forecast list and detail hooks
export function useForecasts(organizationId?: string) {
  return useQuery({
    queryKey: ['forecasts', organizationId],
    queryFn: () => forecastsService.getAll(organizationId),
  });
}

export function useForecast(id: string) {
  return useQuery({
    queryKey: ['forecasts', id],
    queryFn: () => forecastsService.getById(id),
    enabled: !!id,
  });
}

export function useForecastDataPoints(
  id: string,
  filters?: { channel?: string; team?: string; shift?: string }
) {
  return useQuery({
    queryKey: ['forecasts', id, 'dataPoints', filters],
    queryFn: () => forecastsService.getDataPoints(id, filters),
    enabled: !!id,
  });
}

// Generate forecast
export function useGenerateForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: GenerateForecastDto) => forecastsService.generate(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
    },
  });
}

// Update model parameters
export function useUpdateModelParameters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateModelParametersDto }) =>
      forecastsService.updateModelParameters(id, dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', data.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', data.id, 'dataPoints'] });
    },
  });
}

// Regenerate forecast asynchronously (for large datasets)
export function useRegenerateAsync(onProgress?: (status: RegenerationJobStatus) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateModelParametersDto }) =>
      forecastsService.regenerateAsync(id, dto, onProgress),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
    },
  });
}

// Sync actuals
export function useSyncActuals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => forecastsService.syncActuals(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', id, 'accuracy'] });
    },
  });
}

// Get accuracy metrics
export function useForecastAccuracy(id: string) {
  return useQuery({
    queryKey: ['forecasts', id, 'accuracy'],
    queryFn: () => forecastsService.getAccuracy(id),
    enabled: !!id,
  });
}

// Get daily accuracy breakdown
export function useDailyAccuracy(id: string) {
  return useQuery({
    queryKey: ['forecasts', id, 'accuracy', 'daily'],
    queryFn: () => forecastsService.getDailyAccuracy(id),
    enabled: !!id,
  });
}

// Get SLA impact
export function useSLAImpact(id: string, params: SLAImpactParams) {
  return useQuery({
    queryKey: ['forecasts', id, 'slaImpact', params],
    queryFn: () => forecastsService.getSLAImpact(id, params),
    enabled: !!id,
  });
}

// Generate schedule from forecast
export function useGenerateScheduleFromForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: GenerateScheduleParams }) =>
      forecastsService.generateSchedule(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

// Export CSV
export function useExportForecastCSV() {
  return useMutation({
    mutationFn: async (id: string) => {
      const blob = await forecastsService.exportCSV(id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `forecast-${id}-${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

// Import CSV
export function useImportForecastCSV() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      forecastsService.importCSV(id, file),
    onSuccess: (_data, variables) => {
      // Invalidate the forecasts list so the updated date range is reflected
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Import Historical CSV (daily format) - now supports async processing with progress
export function useImportHistoricalCSV(onProgress?: (status: ImportJobStatus) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      file,
      intervalMinutes,
      replaceExisting,
    }: {
      id: string;
      file: File;
      intervalMinutes?: number;
      replaceExisting?: boolean;
    }) => forecastsService.importHistoricalCSV(id, file, { intervalMinutes, replaceExisting }, onProgress),
    onSuccess: (_data, variables) => {
      // Invalidate the forecasts list so the updated date range is reflected
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Delete forecast
export function useDeleteForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => forecastsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
    },
  });
}

// Publish forecast
export function usePublishForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, force = false }: { id: string; force?: boolean }) =>
      forecastsService.publish(id, force),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Unpublish forecast
export function useUnpublishForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => forecastsService.unpublish(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', id, 'dataPoints'] });
    },
  });
}

// Activate forecast
export function useActivateForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => forecastsService.activate(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', id] });
    },
  });
}

// Archive forecast
export function useArchiveForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => forecastsService.archive(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', id] });
    },
  });
}

// Compare models
export function useCompareModels() {
  return useMutation({
    mutationFn: (dto: GenerateForecastDto) => forecastsService.compareModels(dto),
  });
}

// Mark outliers
export function useMarkOutliers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: MarkOutlierDto }) =>
      forecastsService.markOutliers(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Remove outliers
export function useRemoveOutliers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, timestamps }: { id: string; timestamps: string[] }) =>
      forecastsService.removeOutliers(id, timestamps),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Edit forecast values
export function useEditForecastValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: EditForecastValuesDto }) =>
      forecastsService.editForecastValues(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Edit published forecast
export function useEditPublishedForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: EditForecastValuesDto }) =>
      forecastsService.editPublishedForecast(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Smooth forecast
export function useSmoothForecast() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: SmoothForecastDto }) =>
      forecastsService.smoothForecast(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'dataPoints'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.id, 'accuracy'] });
    },
  });
}

// Forecast Assumptions hooks
export function useForecastAssumptions(forecastId: string) {
  return useQuery({
    queryKey: ['forecasts', forecastId, 'assumptions'],
    queryFn: () => forecastsService.getAssumptions(forecastId),
    enabled: !!forecastId,
  });
}

export function useForecastFactorBreakdown(forecastId: string) {
  return useQuery({
    queryKey: ['forecasts', forecastId, 'factor-breakdown'],
    queryFn: () => forecastsService.getFactorBreakdown(forecastId),
    enabled: !!forecastId,
  });
}

export function useCreateAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ forecastId, dto }: { forecastId: string; dto: CreateAssumptionDto }) =>
      forecastsService.createAssumption(forecastId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId, 'assumptions'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId, 'factor-breakdown'] });
    },
  });
}

export function useUpdateAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      forecastId,
      assumptionId,
      dto,
    }: {
      forecastId: string;
      assumptionId: string;
      dto: UpdateAssumptionDto;
    }) => forecastsService.updateAssumption(forecastId, assumptionId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId, 'assumptions'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId, 'factor-breakdown'] });
    },
  });
}

export function useDeleteAssumption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ forecastId, assumptionId }: { forecastId: string; assumptionId: string }) =>
      forecastsService.deleteAssumption(forecastId, assumptionId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId, 'assumptions'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId, 'factor-breakdown'] });
    },
  });
}
